import { useRef, useState, useEffect } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowUpRight, Star, Check } from "lucide-react";
import { PhoneMockup } from "./PhoneMockup";
import { ROUTES } from "../config/routes";

const HEAD_LINES = ["One link.", "Every world."];

const sanitizeHandle = (v) => v.toLowerCase().replace(/[^a-z0-9._]/g, "").slice(0, 20);

const lineParent = {
  hidden: {},
  show: { transition: { staggerChildren: 0.12, delayChildren: 0.15 } },
};
const lineChild = {
  hidden: { y: "110%" },
  show: { y: "0%", transition: { duration: 0.9, ease: [0.22, 1, 0.36, 1] } },
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const phoneY = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const phoneRotate = useTransform(scrollYProgress, [0, 1], [0, -4]);

  const [handle, setHandle] = useState("alex.wav");
  const [buildKey, setBuildKey] = useState(0);
  const [building, setBuilding] = useState(false);
  const firstRun = useRef(true);

  useEffect(() => {
    if (firstRun.current) {
      firstRun.current = false;
      return;
    }
    setBuilding(true);
    const t = setTimeout(() => {
      setBuildKey((k) => k + 1);
      setBuilding(false);
    }, 450);
    return () => clearTimeout(t);
  }, [handle]);

  const previewHandle = handle || "yourname";

  return (
    <section
      id="top"
      ref={ref}
      data-testid="hero"
      className="relative overflow-hidden px-6 pb-24 pt-36 md:px-12 lg:px-24 lg:pt-44"
    >
      {/* ambient lime glow */}
      <div className="pointer-events-none absolute -right-40 -top-20 h-[520px] w-[520px] rounded-full bg-lime/25 blur-[140px]" />

      <div className="relative z-10 mx-auto grid max-w-6xl grid-cols-1 items-center gap-16 lg:grid-cols-2">
        <div>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="mb-7 inline-flex items-center gap-2 rounded-full border border-line bg-surface px-3.5 py-1.5 text-xs font-medium text-inkMuted shadow-soft"
          >
            <Star size={12} className="fill-lime text-lime" />
            Trusted by 240,000+ creators worldwide
          </motion.div>

          <motion.h1
            variants={lineParent}
            initial="hidden"
            animate="show"
            className="font-display text-6xl font-semibold leading-[0.92] tracking-tighter text-ink md:text-7xl lg:text-8xl"
          >
            {HEAD_LINES.map((line, i) => (
              <span key={i} className="block overflow-hidden pb-[0.08em]">
                <motion.span variants={lineChild} className="block">
                  {i === 1 ? (
                    <span className="relative inline-block">
                      {line}
                      <motion.span
                        initial={{ scaleX: 0 }}
                        animate={{ scaleX: 1 }}
                        transition={{ duration: 0.7, delay: 1.1, ease: [0.65, 0, 0.35, 1] }}
                        className="absolute -bottom-1 left-0 h-[6px] w-full origin-left bg-lime"
                      />
                    </span>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.9 }}
            className="mt-7 max-w-md text-base leading-relaxed text-inkMuted md:text-lg"
          >
            BREELINK turns your bio into a full destination — music, video, bookings,
            products and links, all under one beautifully branded page.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.95 }}
            className="mt-8 max-w-md"
          >
            <div className="flex items-center gap-2 rounded-full border border-line bg-surface py-2 pl-4 pr-2 shadow-soft transition-colors focus-within:border-lime">
              <span className="text-sm font-semibold text-inkMuted">breel.ink/</span>
              <input
                data-testid="handle-input"
                value={handle}
                onChange={(e) => setHandle(sanitizeHandle(e.target.value))}
                placeholder="yourname"
                spellCheck={false}
                autoComplete="off"
                aria-label="Choose your BREELINK handle"
                className="min-w-0 flex-1 bg-transparent text-sm font-semibold text-ink outline-none placeholder:text-inkMuted/60"
              />
              <span
                data-testid="handle-status"
                className="flex shrink-0 items-center gap-1.5 rounded-full bg-surfaceAlt px-3 py-1.5 text-xs font-medium text-inkMuted"
              >
                {building ? (
                  <>
                    <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-lime" />
                    Building
                  </>
                ) : (
                  <>
                    <Check size={12} className="text-lime" />
                    Live
                  </>
                )}
              </span>
            </div>
            <p className="mt-2 pl-1 text-xs text-inkMuted">
              Type a handle to watch your page build live →
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 1.05 }}
            className="mt-6 flex flex-col items-start gap-3 sm:flex-row sm:items-center"
          >
            <a
              href={handle ? `${ROUTES.register}?username=${encodeURIComponent(handle)}` : ROUTES.register}
              data-testid="hero-cta-primary"
              className="group inline-flex items-center gap-2 rounded-full bg-lime px-7 py-4 text-sm font-semibold text-limeText transition-transform hover:-translate-y-0.5"
            >
              Build your BREELINK
              <ArrowUpRight size={16} className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
            <a
              href="#features"
              data-testid="hero-cta-secondary"
              className="strike-link inline-flex items-center gap-2 px-2 py-4 text-sm font-semibold text-ink"
            >
              See how it works
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 1.25 }}
            className="mt-10 flex items-center gap-6 text-xs font-medium uppercase tracking-[0.18em] text-inkMuted"
          >
            <span>Free plan available</span>
            <span className="h-1 w-1 rounded-full bg-lime" />
            <span>No code needed</span>
          </motion.div>
        </div>

        <motion.div
          style={{ y: phoneY, rotate: phoneRotate }}
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}
          className="flex justify-center lg:justify-end"
        >
          <PhoneMockup handle={previewHandle} buildKey={buildKey} />
        </motion.div>
      </div>
    </section>
  );
};
